import type {
    Viewport,
    ViewportFlags,
    VisibleWidgetRef,
    Widget,
    WidgetInputData,
    WidgetLayoutEntry,
    WidgetList,
    WidgetSpacingContext,
} from '../entities/types';

export function isHiddenOnMobile(widget: Widget): boolean {
    return !!widget?.isMobileHidden;
}

export function isHiddenOnDesktop(widget: Widget): boolean {
    return !!widget?.isDesktopHidden;
}

function isHiddenOnViewport(widget: Widget, viewport: Viewport): boolean {
    return viewport === 'mobile' ? isHiddenOnMobile(widget) : isHiddenOnDesktop(widget);
}

export function findPreviousVisibleWidget(
    widgets: WidgetList,
    index: number,
    viewport: Viewport
): VisibleWidgetRef | undefined {
    for (let i = index - 1; i >= 0; i--) {
        if (!isHiddenOnViewport(widgets[i], viewport)) {
            return { widget: widgets[i], index: i };
        }
    }

    return undefined;
}

export function findNextVisibleWidget(
    widgets: WidgetList,
    index: number,
    viewport: Viewport
): VisibleWidgetRef | undefined {
    for (let i = index + 1; i < widgets.length; i++) {
        if (!isHiddenOnViewport(widgets[i], viewport)) {
            return { widget: widgets[i], index: i };
        }
    }

    return undefined;
}

function getReducedTopSpacingForViewport(
    widget: Widget & WidgetInputData,
    widgets: WidgetList,
    index: number,
    viewport: Viewport,
    context: WidgetSpacingContext
): boolean {
    const previousWidget = findPreviousVisibleWidget(widgets, index, viewport)?.widget;
    const widgetSupportsReducedSpacings = !context.widgetsNotSupportedReduceSpacings.includes(widget.class_name);
    const isLogoroll = previousWidget?.class_name === 'logoroll';
    const isMediaSectionInSidebar = widget.class_name === 'media_section_widget' && context.isSidebarLayout;

    if (!widgetSupportsReducedSpacings || !previousWidget || isLogoroll || isMediaSectionInSidebar) {
        return false;
    }

    const currentWidgetColor = context.getWidgetColorScheme(widget);
    const previousWidgetColor = context.getWidgetColorScheme(previousWidget);

    if (currentWidgetColor === undefined && previousWidgetColor === undefined) {
        return false;
    }

    return currentWidgetColor === previousWidgetColor;
}

export function getReducedTopSpacings(
    widgets: WidgetList,
    index: number,
    context: WidgetSpacingContext
): ViewportFlags {
    const widget = widgets[index];

    return {
        mobile: getReducedTopSpacingForViewport(widget, widgets, index, 'mobile', context),
        desktop: getReducedTopSpacingForViewport(widget, widgets, index, 'desktop', context),
    };
}

function isFiguredBackgroundAllowedForViewport(
    widgets: WidgetList,
    index: number,
    viewport: Viewport,
    reducedTopSpacings: ViewportFlags[]
): boolean {
    const nextWidget = findNextVisibleWidget(widgets, index, viewport);

    if (!nextWidget) {
        return true;
    }

    return !reducedTopSpacings[nextWidget.index][viewport];
}

export function getFiguredBackgroundAllowed(
    widgets: WidgetList,
    index: number,
    reducedTopSpacings: ViewportFlags[]
): ViewportFlags {
    return {
        mobile: isFiguredBackgroundAllowedForViewport(widgets, index, 'mobile', reducedTopSpacings),
        desktop: isFiguredBackgroundAllowedForViewport(widgets, index, 'desktop', reducedTopSpacings),
    };
}

export function buildWidgetLayoutEntries(context: WidgetSpacingContext): WidgetLayoutEntry[] {
    const renderContext = context.isSidebarLayout ? 'sidebar' : 'default';
    const { widgets } = context;
    const reducedTopSpacings = widgets.map((_, index) => getReducedTopSpacings(widgets, index, context));

    return widgets.map((widget, index) => ({
        widget,
        index,
        hasWrapper: !context.isSidebarLayout,
        renderContext,
        reducedTopSpacing: reducedTopSpacings[index],
        figuredBackgroundAllowed: getFiguredBackgroundAllowed(widgets, index, reducedTopSpacings),
    }));
}
